import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Receipt,
  Plus,
  Wallet,
  CheckCircle2,
  Clock,
  CircleDollarSign,
  Hourglass,
  AlertTriangle,
  Ban,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  SlidersHorizontal,
  X,
  BellRing,
  CalendarClock,
} from 'lucide-react';
import useCobroStore, { narrowingOf, narrowingPatch } from '../../../store/cobro';
import useAuth from '../../../hooks/useAuth';
import { getActiveClientsApi } from '../../../api/company';
import { listOperatorHoldings } from '../../../api/operatorHoldings';
import { showToast } from '../../../utils/showToast';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { Button } from '../../../components/ui/Button';
import { CopyButton } from '../../../components/ui/CopyButton';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { Select } from '../../../components/ui/Select';
import { SearchableSelect } from '../../../components/ui/SearchableSelect';
import { PageHeader } from '../../../components/ui/PageHeader';
import { EmptyState } from '../../../components/ui/EmptyState';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { KpiFilterCards } from '../../../components/ui/KpiFilterCards';
import DateRangeFilter from '../../../components/dateRangeFilter/DateRangeFilter';
import { useIsMobile } from '../../../hooks/useIsMobile';
import PaymentStatusBadge from './components/PaymentStatusBadge';
import { DueDateHint } from './components/DueDateInfo';
import GenerateCobroWizard from './components/GenerateCobroWizard';
import CobroDashboard from './components/CobroDashboard';
import ScheduleReminderModal from './components/ScheduleReminderModal';
import CommerceCollectionRuleDrawer from './components/CommerceCollectionRuleDrawer';
import { formatCommerceOptionLabel } from './components/commerceOptionLabel';
import { matchCommerce, toCommerceOption } from '../../../utils/commerceSearch';
import { isHoldingNote, noteHoldingId } from './utils/holdings';

import {
  formatCurrency,
  formatDate,
  CHARGE_MODE_LABELS,
  CHARGE_MODE_FILTERS,
  resolveCompanyName,
  resolveOperatorCurrency,
  dueDateStatus,
} from './utils/cobroHelpers';
import {
  PAYMENT_METHODS,
  amountDueOf,
  hasValidPrecision,
  paidAmountOf,
  paidAtIso,
  parseAmountInput,
  paymentErrorMessage,
  paymentMethodLabelKey,
  roundingTolerance,
  todayYMD,
  tomorrowYMD,
} from './utils/payments';

// [checkbox] · Folio · Comercio · Periodo · Modo · Total · Saldo · Estado · Acciones
const GRID_COLS = '36px 0.7fr 1.5fr 1.3fr 0.9fr 1fr 1fr 1fr auto';
const PAGE_SIZE = 25;

const PENDING_STATES = ['unpaid', 'partially_paid', 'overdue'];

// Estados que el marcado masivo puede tocar. Coinciden 1:1 con los que acepta el endpoint:
// ofrecer otros solo produciría `skipped` y le haría perder el tiempo al operador.
const MARKABLE_STATES = ['unpaid', 'overdue', 'partially_paid'];
const isMarkable = (n) => MARKABLE_STATES.includes(n.paymentStatus);

// Por qué una nota no se puede marcar. Se muestra en el title del checkbox deshabilitado:
// sin esto, una casilla apagada sin explicación parece un bug.
const NOT_MARKABLE_REASON = {
  paid: 'Ya está pagada',
  cancelled: 'Está anulada',
  // Estas van por "confirmar comprobante" en el detalle: ese camino además cierra el
  // registro de pago del comercio, que este marcado masivo no toca.
  in_review: 'Tiene un comprobante en revisión: confirmalo desde el detalle',
};

// Cards-filtro por estado de pago (idea tomada de /admin/pedidos).
// "Vencidas" no es un paymentStatus (billing la deriva de la fecha de vencimiento): esa
// tarjeta es un filtro del SERVER que achica el lote (`filters.overdue`), las demás filtran
// en el cliente.
const OVERDUE_CARD = 'overdue';
const STATUS_CARDS = [
  { value: '', label: 'Todas', icon: Receipt, tone: 'brand' },
  { value: 'unpaid', label: 'Impagas', icon: Clock, tone: 'warning' },
  { value: 'partially_paid', label: 'Parciales', icon: CircleDollarSign, tone: 'info' },
  { value: 'in_review', label: 'En revisión', icon: Hourglass, tone: 'info' },
  { value: 'paid', label: 'Pagadas', icon: CheckCircle2, tone: 'success' },
  { value: OVERDUE_CARD, label: 'Vencidas', icon: AlertTriangle, tone: 'error' },
  { value: 'cancelled', label: 'Anuladas', icon: Ban, tone: 'neutral' },
];

// Filtros que achican el lote y se prenden desde las tarjetas del resumen de cobranza (no
// desde las de estado): se nombran en el chip "Filtrado por" con el rótulo de su tarjeta.
const DASHBOARD_FILTER_LABELS = {
  dueSoon: 'Por vencer (7 días)',
  multipleUnpaid: 'Comercios con más de 1 nota impaga',
};

// react-select entrega `{ label, value, data }` y los campos del comercio quedan en `data`.
const filterCommerceOption = (option, query) => matchCommerce(option.data, query);

// Presets de fecha adecuados a la facturación periódica (mes/quincena).
const DATE_PRESETS_COBRO = [
  { key: 'thisMonth', quick: true },
  { key: 'lastMonth', quick: true },
  { key: '30d', quick: true },
  { key: '90d', quick: false },
];

// Por qué billing rechazó una nota del marcado masivo, en el orden en que se muestra el
// detalle. Cada rechazo es un hecho: agruparlos por motivo deja decir "1 ya pagadas · 2
// anuladas" en vez de un "no se marcaron" sin causa.
const SKIPPED_REASON_KEYS = [
  ['already_paid', '{{n}} ya pagadas'],
  ['cancelled', '{{n}} anuladas'],
  ['in_review', '{{n}} con pago en revisión'],
  ['not_found', '{{n}} no encontradas'],
];

/** "2 no se marcaron: 1 ya pagadas · 1 anuladas" a partir del `skipped` del backend. */
const skippedReasonsMessage = (skipped, t) => {
  const counts = new Map();
  for (const s of skipped ?? []) {
    const reason = s?.reason ?? '';
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  const parts = [];
  for (const [reason, key] of SKIPPED_REASON_KEYS) {
    const n = counts.get(reason) ?? 0;
    if (!n) continue;
    parts.push(t(key, { n }));
    counts.delete(reason);
  }
  // Un motivo que este front no conoce (billing nuevo) se cuenta igual: la nota se omitió.
  const unknown = [...counts.values()].reduce((s, n) => s + n, 0);
  if (unknown) parts.push(t('{{n}} por otro motivo', { n: unknown }));
  return t('{{n}} no se marcaron: {{detalle}}', {
    n: (skipped ?? []).length,
    detalle: parts.join(' · '),
  });
};

// ¿El periodo de la nota se solapa con [from, to]? (fechas YMD del filtro).
// El endpoint de listado no filtra por fecha, así que se resuelve en el front
// contra el periodo de la nota (fallback a createdAt).
const noteInRange = (n, from, to) => {
  if (!from || !to) return true;
  const fromTs = new Date(`${from}T00:00:00`).getTime();
  const toTs = new Date(`${to}T23:59:59`).getTime();
  const s = new Date(n.periodStart ?? n.createdAt ?? 0).getTime();
  const e = new Date(n.periodEnd ?? n.periodStart ?? n.createdAt ?? 0).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return true;
  return s <= toTs && e >= fromTs;
};

// Id del comercio de una nota, poblado o suelto (mismo criterio que resolveCompanyName).
const noteCompanyId = (n) =>
  String((typeof n.companyId === 'object' ? n.companyId?._id : n.companyId) ?? '');

/** Chip "Filtrado por: …" con su botón para quitar el filtro. */
function FilterChip({ label, removeLabel, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-brand-500 bg-brand-50/60 py-1 pl-3 pr-1 text-xs font-medium text-brand-700">
      {label}
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        title={removeLabel}
        className="inline-flex items-center justify-center rounded-full p-1 hover:bg-brand-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <X className="w-3.5 h-3.5" aria-hidden />
      </button>
    </span>
  );
}

const CobroNotesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const logisticCompanyId = user?.logisticCompany?._id ?? null;
  const operatorCurrency = resolveOperatorCurrency(user?.logisticCompany);

  const {
    notes,
    notesCount,
    notesScope,
    notesError,
    loading,
    filters,
    setFilters,
    setNarrowing,
    fetchNotes,
    markNotesPaid,
    markingPaid,
    allocatePayment,
    allocating,
    listCredits,
    dashboard,
    fetchDashboard,
  } = useCobroStore((s) => ({
    notes: s.notes,
    notesCount: s.notesCount,
    notesScope: s.notesScope,
    notesError: s.notesError,
    loading: s.loading,
    filters: s.filters,
    setFilters: s.setFilters,
    setNarrowing: s.setNarrowing,
    fetchNotes: s.fetchNotes,
    markNotesPaid: s.markNotesPaid,
    markingPaid: s.markingPaid,
    allocatePayment: s.allocatePayment,
    allocating: s.allocating,
    listCredits: s.listCredits,
    // Solo el resumen de ESTE operador (ver dashboardOwnerId en el store).
    dashboard: s.dashboardOwnerId === logisticCompanyId ? s.dashboard : null,
    fetchDashboard: s.fetchDashboard,
  }));

  const [wizardOpen, setWizardOpen] = useState(false);
  const [companyMap, setCompanyMap] = useState({});
  const [activeCompanyOptions, setActiveCompanyOptions] = useState([]);
  // Holdings del operador (contracts), para el filtro por holding.
  const [operatorHoldingOptions, setOperatorHoldingOptions] = useState([]);
  // Deudor elegido desde "Mayores morosos" ({ debtorType, companyId, holdingId, companyName }).
  // Se recuerda aparte para mostrar el chip "Filtrado por" con su nombre aunque el comercio no
  // tenga contrato activo.
  const [dashboardFilter, setDashboardFilter] = useState(null);
  // Regla de cobranza abierta en el panel lateral desde "Mayores morosos".
  const [ruleDrawer, setRuleDrawer] = useState(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const listRef = useRef(null);
  const [datePreset, setDatePreset] = useState(''); // '' = sin filtro de fecha
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false); // filtros colapsados en mobile

  // El filtro de servidor que achica el lote ('' = ninguno): "Vencidas", "Por vencer" o
  // "+1 impaga". Uno solo, aunque el store tuviera más de un flag prendido.
  const narrowing = narrowingOf(filters);

  // El server filtra por comercio u holding, modo y el filtro que achica el lote → re-fetch
  // sólo cuando cambian esos.
  useEffect(() => {
    fetchNotes();
  }, [fetchNotes, filters.chargeMode, filters.companyId, filters.holdingId, narrowing]);

  // Cualquier cambio de filtro vuelve a la primera página (paginación cliente).
  useEffect(() => {
    setPage(1);
  }, [filters.paymentStatus, filters.chargeMode, filters.companyId, filters.holdingId, narrowing, dateFrom, dateTo]);

  // Catálogo de comercios: para el filtro y para resolver nombres en la tabla.
  useEffect(() => {
    if (!logisticCompanyId) return;
    let active = true;
    getActiveClientsApi(logisticCompanyId)
      .then((data) => {
        if (!active || !Array.isArray(data)) return;
        setCompanyMap(Object.fromEntries(data.map((c) => [c._id, c.name || ''])));
        setActiveCompanyOptions(data.map((c) => toCommerceOption(c, t('Sin nombre'))));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [logisticCompanyId, t]);

  // Holdings del operador para el filtro por holding. Si la lectura falla, el filtro se arma
  // igual con los holdings que aparecen en las notas cargadas (ver holdingOptions).
  useEffect(() => {
    if (!logisticCompanyId) return;
    let active = true;
    Promise.resolve(listOperatorHoldings())
      .then((res) => {
        const rows = res?.data;
        if (!active || !Array.isArray(rows)) return;
        setOperatorHoldingOptions(
          rows.map((h) => ({ value: String(h.id), label: h.name || t('Sin nombre') })),
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [logisticCompanyId, t]);

  // Opciones del filtro de comercio: los de contrato activo MÁS los que aparecen en las notas
  // cargadas y en "Mayores morosos". active-contracts solo trae contratos activos, y los
  // morosos más probables son justamente excomercios: sin su opción, filtrar por uno deja el
  // selector en blanco con un filtro activo que no se ve ni se puede limpiar.
  const companyOptions = useMemo(() => {
    const active = new Set(activeCompanyOptions.map((o) => o.value));
    const extras = new Map();
    const addExtra = (companyId, companyName) => {
      const id = String(companyId ?? '');
      if (!id || active.has(id)) return;
      const known = extras.get(id);
      // Un comercio visto primero sin nombre se reemplaza cuando aparece con nombre.
      if (known && (known.name || !companyName)) return;
      extras.set(id, toCommerceOption({ _id: id, name: companyName }, t('Sin nombre')));
    };
    notes.forEach((n) => addExtra(noteCompanyId(n), n.companyName));
    (dashboard?.topDebtors ?? []).forEach((d) => addExtra(d.companyId, d.companyName));
    if (dashboardFilter?.debtorType !== 'holding') {
      addExtra(dashboardFilter?.companyId, dashboardFilter?.companyName);
    }
    const sortedExtras = [...extras.values()].sort((a, b) => a.label.localeCompare(b.label, 'es'));
    return [...activeCompanyOptions, ...sortedExtras];
  }, [activeCompanyOptions, notes, dashboard, dashboardFilter, t]);

  // Opciones del filtro por holding: los del operador MÁS los que aparecen en las notas
  // cargadas, en "Mayores morosos", en el moroso elegido o en el filtro ya puesto. Mismo
  // motivo que con los comercios: un filtro sin su opción quedaría invisible y sin forma de
  // quitarlo desde el selector.
  const holdingOptions = useMemo(() => {
    const known = new Set(operatorHoldingOptions.map((o) => o.value));
    const extras = new Map();
    const addExtra = (holdingId, name) => {
      const id = String(holdingId ?? '');
      if (!id || known.has(id)) return;
      // Un holding visto primero sin nombre se reemplaza cuando aparece con nombre.
      if (extras.get(id)?.label || (extras.has(id) && !name)) return;
      extras.set(id, { value: id, label: name || '' });
    };
    notes.forEach((n) => addExtra(noteHoldingId(n), n.companyName));
    (dashboard?.topDebtors ?? []).forEach((d) => addExtra(d.holdingId, d.companyName));
    if (dashboardFilter?.debtorType === 'holding') {
      addExtra(dashboardFilter.holdingId, dashboardFilter.companyName);
    }
    addExtra(filters.holdingId, '');
    const sortedExtras = [...extras.values()]
      .map((o) => ({ ...o, label: o.label || t('Sin nombre') }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es'));
    return [...operatorHoldingOptions, ...sortedExtras];
  }, [operatorHoldingOptions, notes, dashboard, dashboardFilter, filters.holdingId, t]);

  const dashboardFilterIsHolding = dashboardFilter?.debtorType === 'holding';
  const dashboardFilterId = dashboardFilterIsHolding
    ? dashboardFilter?.holdingId
    : dashboardFilter?.companyId;
  const dashboardFilterActive =
    !!dashboardFilterId &&
    (dashboardFilterIsHolding ? filters.holdingId : filters.companyId) === dashboardFilterId;
  const dashboardFilterName = dashboardFilterActive
    ? dashboardFilter.companyName ||
      (dashboardFilterIsHolding ? holdingOptions : companyOptions).find(
        (o) => o.value === dashboardFilterId,
      )?.label ||
      t('Sin nombre')
    : '';

  // Click en un moroso: filtra la lista por ese comercio u holding (todas sus notas, sin
  // ningún filtro que achique el lote) y la trae a la vista. Comercio y holding se excluyen:
  // la nota de un holding no tiene comercio, así que combinados no traerían nada.
  const handleSelectDebtor = (debtor) => {
    setDashboardFilter(debtor);
    const target =
      debtor.debtorType === 'holding'
        ? { holdingId: debtor.holdingId, companyId: '' }
        : { companyId: debtor.companyId, holdingId: '' };
    setFilters({ ...target, paymentStatus: '', ...narrowingPatch('') });
    listRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };
  const clearDashboardFilter = () => {
    setDashboardFilter(null);
    setFilters(dashboardFilterIsHolding ? { holdingId: '' } : { companyId: '' });
  };

  // Tarjetas del resumen que filtran la lista ("Monto vencido", "Por vencer", "+1 impaga"). Son excluyentes
  // entre sí y con "Vencidas" (setNarrowing apaga las otras) y se combinan con el comercio y
  // el modo. Un segundo clic en la tarjeta activa la apaga.
  const toggleDashboardListFilter = (key) => setNarrowing(narrowing === key ? '' : key);
  // Sale del store y no de estado local: al volver del detalle de una nota el filtro sigue
  // puesto, y el chip tiene que seguir diciéndolo.
  const dashboardListFilterLabel = DASHBOARD_FILTER_LABELS[narrowing]
    ? t(DASHBOARD_FILTER_LABELS[narrowing])
    : '';

  // 1) Filtro por fecha (cliente). 2) Conteos por estado sobre ese set.
  const dateFiltered = useMemo(
    () => notes.filter((n) => noteInRange(n, dateFrom, dateTo)),
    [notes, dateFrom, dateTo],
  );

  // Último lote SIN filtro que lo achique (con su comercio u holding y modo). Con "Vencidas",
  // "Por vencer" o "+1 impaga" activa el server devuelve solo esas notas: contar las tarjetas de
  // estado sobre ese lote daría "Pagadas 0" y "Todas = N". Se cuentan sobre este lote, que es
  // lo que mostraban antes del clic. Una mutación (marcar pagadas, generar notas) lo
  // invalida: con el filtro activo solo se vuelve a pedir el lote achicado, y este quedaría
  // con los números de antes → '—'.
  const [fullBatch, setFullBatch] = useState(null);
  useEffect(() => {
    if (notesScope && !notesScope.narrowing) {
      setFullBatch({
        companyId: notesScope.companyId,
        holdingId: notesScope.holdingId,
        chargeMode: notesScope.chargeMode,
        notes,
      });
    }
  }, [notes, notesScope]);

  // Base de los conteos por estado (con el filtro de fecha aplicado), o null si no se
  // conoce: un filtro que achica el lote se activó y después cambió el comercio, el holding o
  // el modo, así que no hay un lote completo de ese alcance. Las tarjetas muestran '—' antes
  // que un número falso.
  const statusCountBase = useMemo(() => {
    if (!notesScope?.narrowing) return dateFiltered;
    const sameScope =
      fullBatch &&
      fullBatch.companyId === notesScope.companyId &&
      fullBatch.holdingId === notesScope.holdingId &&
      fullBatch.chargeMode === notesScope.chargeMode;
    return sameScope ? fullBatch.notes.filter((n) => noteInRange(n, dateFrom, dateTo)) : null;
  }, [notesScope, dateFiltered, fullBatch, dateFrom, dateTo]);
  const statusCountTotal = statusCountBase ? statusCountBase.length : null;

  // Conteo de "Vencidas". Nadie persiste paymentStatus 'overdue', así que no sale del
  // conteo por estado. Se usa el número más exacto disponible para lo que se ve:
  //   · con el lote de "Vencidas" cargado, el count del server (el lote no lo trunca);
  //   · sin filtros de comercio/modo/fecha, el del dashboard: el lote de 500 corta por fecha
  //     de creación y dejaría afuera justo las más viejas;
  //   · con filtros, las marcadas vencidas de la base de las tarjetas de estado (el lote
  //     completo si otro filtro achicó el que se ve, o '—' si no se conoce).
  // Se decide con el filtro del LOTE (`notesScope`), no con el pedido: mientras carga otro, o
  // si esa carga falló, `notesCount` es el total de otro lote. Si no coinciden, null → '—'.
  const overdueCount = useMemo(() => {
    if ((notesScope?.narrowing ?? '') !== narrowing) return null;
    const dateFilterOn = !!(dateFrom && dateTo);
    const countOverdue = (list) => list.filter((n) => n.isOverdue === true).length;
    if (narrowing === OVERDUE_CARD) return dateFilterOn ? countOverdue(dateFiltered) : notesCount;
    const baseCount = statusCountBase ? countOverdue(statusCountBase) : null;
    const scoped = !!(filters.companyId || filters.holdingId || filters.chargeMode || dateFilterOn);
    if (!scoped && dashboard?.overdue) return dashboard.overdue.notes ?? baseCount;
    return baseCount;
  }, [dateFiltered, dateFrom, dateTo, notesScope, narrowing, statusCountBase, filters.companyId, filters.holdingId, filters.chargeMode, notesCount, dashboard]);

  const counts = useMemo(() => {
    const c = {};
    if (statusCountBase) {
      for (const n of statusCountBase) c[n.paymentStatus] = (c[n.paymentStatus] || 0) + 1;
    } else {
      for (const card of STATUS_CARDS) c[card.value] = null;
    }
    c[OVERDUE_CARD] = overdueCount;
    return c;
  }, [statusCountBase, overdueCount]);

  // Tarjeta activa: "Vencidas" es un filtro aparte (server) del estado de pago (cliente).
  // Con "Por vencer" o "+1 impaga" (prendidos desde el resumen) ninguna tarjeta de estado
  // queda activa: el lote es de ese filtro, no de un estado.
  const activeStatusCard =
    narrowing === OVERDUE_CARD ? OVERDUE_CARD : narrowing ? null : filters.paymentStatus || '';
  // Elegir un estado apaga cualquier filtro que achique el lote: las tarjetas cuentan el lote
  // completo, y un estado sobre un lote achicado mostraría otra cosa que su número.
  const selectStatusCard = (value) =>
    value === OVERDUE_CARD
      ? setNarrowing(OVERDUE_CARD)
      : setFilters({ ...narrowingPatch(''), paymentStatus: value });

  // 3) Filtro por estado (cliente) sobre el set ya filtrado por fecha. Con un filtro que
  // achica el lote, el server ya filtró. "Vencidas" además re-aplica su flag para no mostrar
  // otra cosa si un billing viejo ignorara `overdue`; "Por vencer" y "+1 impaga" no tienen un
  // derivado por nota que se pueda re-aplicar, así que billing tiene que desplegar antes.
  const filtered = useMemo(() => {
    if (narrowing === OVERDUE_CARD) return dateFiltered.filter((n) => n.isOverdue === true);
    return filters.paymentStatus
      ? dateFiltered.filter((n) => n.paymentStatus === filters.paymentStatus)
      : dateFiltered;
  }, [dateFiltered, narrowing, filters.paymentStatus]);

  // Resumen de la vista filtrada. Cobrado suma lo efectivamente pagado (incluye abonos
  // parciales) y Pendiente suma saldos, no el total de la nota.
  const strip = useMemo(() => {
    const total = filtered.reduce((s, n) => s + (n.totalAmount ?? 0), 0);
    const cobrado = filtered.reduce((s, n) => s + paidAmountOf(n), 0);
    const pendiente = filtered
      .filter((n) => PENDING_STATES.includes(n.paymentStatus))
      .reduce((s, n) => s + amountDueOf(n), 0);
    return { total, cobrado, pendiente };
  }, [filtered]);

  // Paginación cliente.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Aviso si el back devolvió menos notas de las que dice tener (tope de lote).
  const truncated = notesCount > notes.length;

  // La última carga de la lista falló: lo cargado es un lote anterior (quizás de otro
  // filtro), así que en vez de esas filas se muestra el error con "Reintentar".
  const loadFailed = !loading && !!notesError;

  // Lote vacío SIN ningún filtro de servidor: el operador todavía no emitió notas. Con un
  // filtro de servidor (comercio, holding, modo, "Vencidas", "Por vencer", "+1 impaga") un
  // lote vacío es "sin resultados", no una invitación a generar la primera nota.
  const noNotesAtAll =
    notes.length === 0 &&
    !narrowing &&
    !filters.companyId &&
    !filters.holdingId &&
    !filters.chargeMode;

  // ── Selección múltiple para marcar como pagadas ──
  // El checkbox de cabecera abarca la PÁGINA visible, no las hasta 500 notas del filtro:
  // marcar como pagado es irreversible, así que la casilla no puede alcanzar filas que el
  // operador no tiene delante. Para el caso masivo real está el atajo explícito de abajo.
  const [selected, setSelected] = useState(() => new Set());

  const pageMarkable = useMemo(() => pageItems.filter(isMarkable).map((n) => n._id), [pageItems]);
  const filteredMarkable = useMemo(() => filtered.filter(isMarkable).map((n) => n._id), [filtered]);

  const allPageSelected = pageMarkable.length > 0 && pageMarkable.every((id) => selected.has(id));
  const somePageSelected = pageMarkable.some((id) => selected.has(id)) && !allPageSelected;

  // Al cambiar de filtro se limpia la selección: si no, quedarían marcadas notas que ya no
  // se ven y el contador de la barra mentiría sobre lo que se está por hacer.
  useEffect(() => {
    setSelected(new Set());
  }, [filters.paymentStatus, filters.chargeMode, filters.companyId, filters.holdingId, narrowing, dateFrom, dateTo]);

  const toggleNote = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (pageMarkable.every((id) => next.has(id))) pageMarkable.forEach((id) => next.delete(id));
      else pageMarkable.forEach((id) => next.add(id));
      return next;
    });

  // Notas seleccionadas, resueltas contra el set filtrado (no contra la página) para poder
  // sumar el monto aunque el operador cambie de página con la selección puesta.
  const selectedNotes = useMemo(
    () => filtered.filter((n) => selected.has(n._id)),
    [filtered, selected],
  );
  // Lo que el marcado masivo registra como pago es el SALDO de cada nota, no su total.
  const selectedAmount = useMemo(
    () => selectedNotes.reduce((s, n) => s + amountDueOf(n), 0),
    [selectedNotes],
  );
  // Las notas llevan su propia moneda. Sumar montos de monedas distintas daría un número
  // sin sentido, así que el total sólo se rotula con la moneda cuando hay una sola.
  const currencyOfSelection = useMemo(() => {
    const monedas = new Set(selectedNotes.map((n) => n.currency).filter(Boolean));
    return monedas.size === 1 ? [...monedas][0] : undefined;
  }, [selectedNotes]);
  const mixedCurrencies = useMemo(
    () => new Set(selectedNotes.map((n) => n.currency).filter(Boolean)).size > 1,
    [selectedNotes],
  );
  // Las notas de holdings todavía no admiten recordatorios (billing responde 409
  // holding_reminders_not_supported): no viajan al programar, y se dice cuántas quedan fuera.
  const reminderNotes = useMemo(
    () => selectedNotes.filter((n) => !isHoldingNote(n)),
    [selectedNotes],
  );
  const holdingNotesSelected = selectedNotes.length - reminderNotes.length;

  // ── Abono repartido entre varias notas (allocate) ──
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [allocAmount, setAllocAmount] = useState('');
  const [allocDate, setAllocDate] = useState(todayYMD);
  const [allocMethod, setAllocMethod] = useState('transfer');
  const [allocReference, setAllocReference] = useState('');
  const [allocComment, setAllocComment] = useState('');
  const [allocUseCredit, setAllocUseCredit] = useState(false);
  const [allocCreditId, setAllocCreditId] = useState('');
  const [allocCredits, setAllocCredits] = useState([]);
  const [allocCreditsLoading, setAllocCreditsLoading] = useState(false);
  const [allocCreditsError, setAllocCreditsError] = useState('');
  const [allocError, setAllocError] = useState('');

  // Clave del deudor común ('h:<holdingId>' o 'c:<companyId>'); '' si la selección mezcla
  // deudores. allocate exige uno solo.
  const selectionDebtorKey = useMemo(() => {
    const keys = new Set(
      selectedNotes.map((note) =>
        noteHoldingId(note) ? `h:${noteHoldingId(note)}` : `c:${noteCompanyId(note)}`,
      ),
    );
    return keys.size === 1 ? [...keys][0] : '';
  }, [selectedNotes]);
  const singleDebtor = !!selectionDebtorKey;
  // El DTO de allocate acepta entre 1 y 20 notas.
  const tooManyForAllocation = selectedNotes.length > 20;
  const canAllocate =
    selectedNotes.length > 0 && singleDebtor && !mixedCurrencies && !tooManyForAllocation;

  // Las notas se aplican de la más antigua a la más nueva: folio ascendente.
  const allocationNotes = useMemo(
    () =>
      [...selectedNotes].sort((a, b) =>
        String(a.folio ?? '').localeCompare(String(b.folio ?? ''), 'es', { numeric: true }),
      ),
    [selectedNotes],
  );
  const allocationTotalDue = useMemo(
    () => allocationNotes.reduce((sum, note) => sum + amountDueOf(note), 0),
    [allocationNotes],
  );

  // Créditos abiertos del deudor, filtrados por la moneda de la selección.
  useEffect(() => {
    if (!allocateOpen || !singleDebtor || mixedCurrencies) {
      setAllocCredits([]);
      setAllocCreditsError('');
      return undefined;
    }
    const [kind, id] = selectionDebtorKey.split(':');
    const params =
      kind === 'h' ? { holdingId: id, onlyOpen: true } : { companyId: id, onlyOpen: true };
    let active = true;
    setAllocCreditsLoading(true);
    setAllocCreditsError('');
    Promise.resolve(listCredits(params))
      .then((data) => {
        if (active) setAllocCredits(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (active) {
          setAllocCredits([]);
          setAllocCreditsError('No se pudieron cargar los saldos a favor.');
        }
      })
      .finally(() => {
        if (active) setAllocCreditsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [allocateOpen, singleDebtor, mixedCurrencies, selectionDebtorKey, listCredits]);

  const allocationCredits = useMemo(
    () =>
      allocCredits.filter(
        (credit) =>
          (!currencyOfSelection || credit.currency === currencyOfSelection) &&
          (Number(credit.remaining) || 0) > 0,
      ),
    [allocCredits, currencyOfSelection],
  );

  // Con un único crédito disponible se elige solo: evita un paso sin decisión real.
  useEffect(() => {
    if (allocUseCredit && allocationCredits.length === 1 && !allocCreditId) {
      setAllocCreditId(allocationCredits[0]._id);
    }
  }, [allocUseCredit, allocationCredits, allocCreditId]);

  // Reparto en el orden de aplicación: cada nota recibe min(restante, su saldo); lo que
  // sobra queda como saldo a favor (salvo con método 'credit').
  const allocationPreview = useMemo(() => {
    const amount = parseAmountInput(allocAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return { rows: [], applied: 0, leftover: 0 };
    }
    let rest = amount;
    const rows = allocationNotes.map((note) => {
      const due = amountDueOf(note);
      const applied = Math.min(rest, due);
      rest -= applied;
      return { noteId: note._id, folio: note.folio, due, applied };
    });
    let leftover = rest > 1e-9 ? rest : 0;
    // Contrato §3.3: un sobrante dentro de la tolerancia de la última nota se le suma
    // (no crea crédito). Sin esto la vista previa prometería un saldo a favor inexistente.
    if (leftover > 0 && rows.length) {
      const lastNote = allocationNotes[allocationNotes.length - 1];
      if (leftover <= roundingTolerance(lastNote.totalAmount)) {
        rows[rows.length - 1].applied += leftover;
        leftover = 0;
      }
    }
    return { rows, applied: amount - leftover, leftover };
  }, [allocAmount, allocationNotes]);

  const openAllocate = () => {
    setAllocAmount(String(allocationTotalDue || ''));
    setAllocDate(todayYMD());
    setAllocMethod('transfer');
    setAllocReference('');
    setAllocComment('');
    setAllocUseCredit(false);
    setAllocCreditId('');
    setAllocError('');
    setAllocateOpen(true);
  };

  const allocationValidationError = () => {
    if (tooManyForAllocation) return t('El abono admite hasta 20 notas.');
    if (!singleDebtor) return t('Las notas deben ser de un solo deudor.');
    if (mixedCurrencies) return t('Las notas deben estar en la misma moneda.');
    const currency = currencyOfSelection ?? 'CLP';
    const amount = parseAmountInput(allocAmount);
    if (!Number.isFinite(amount) || amount <= 0) return t('El monto debe ser mayor que cero.');
    if (!hasValidPrecision(amount, currency)) {
      return currency === 'CLP'
        ? t('El monto debe ser un número entero para {{currency}}.', { currency })
        : t('El monto debe tener hasta 2 decimales.');
    }
    if (!allocDate) return t('Selecciona la fecha de pago.');
    if (allocDate > tomorrowYMD()) return t('La fecha de pago no puede ser posterior a mañana.');
    if (allocReference.trim().length > 120) {
      return t('La referencia no puede superar los 120 caracteres.');
    }
    if (allocComment.trim().length > 500) {
      return t('El comentario no puede superar los 500 caracteres.');
    }
    if (allocUseCredit) {
      const credit = allocationCredits.find((item) => item._id === allocCreditId);
      if (!credit) return t('Selecciona un saldo a favor.');
      if ((Number(credit.remaining) || 0) < amount) {
        return t('El saldo a favor no alcanza para cubrir el abono.');
      }
      if (allocationPreview.leftover > 0) {
        return t('Con saldo a favor el monto no puede dejar sobrante.');
      }
    }
    return '';
  };

  const handleAllocate = async () => {
    const validation = allocationValidationError();
    if (validation) {
      setAllocError(validation);
      return;
    }
    const currency = currencyOfSelection ?? 'CLP';
    const reference = allocReference.trim();
    const comment = allocComment.trim();
    setAllocError('');
    setFullBatch(null);
    try {
      const res = await allocatePayment({
        amount: parseAmountInput(allocAmount),
        paidAt: paidAtIso(allocDate),
        method: allocUseCredit ? 'credit' : allocMethod,
        ...(reference ? { reference } : {}),
        ...(comment ? { comment } : {}),
        noteIds: allocationNotes.map((note) => note._id),
        ...(allocUseCredit ? { creditId: allocCreditId } : {}),
      });
      setAllocateOpen(false);
      setSelected(new Set());
      fetchDashboard(logisticCompanyId);
      const applied = res?.applied?.length ?? allocationNotes.length;
      showToast(t('Abono aplicado a {{n}} nota(s).', { n: applied }), 'success');
      if (res?.credit?.amount > 0) {
        showToast(
          t('Queda un saldo a favor de {{amount}}.', {
            amount: formatCurrency(res.credit.amount, currency),
          }),
          'info',
        );
      }
    } catch (err) {
      setAllocError(
        paymentErrorMessage(err, t, t('No se pudo registrar el abono.'), currency),
      );
    }
  };

  const [confirmPaidOpen, setConfirmPaidOpen] = useState(false);

  const handleMarkPaid = async () => {
    // Antes de la mutación: si la recarga que dispara trae un lote completo, lo repone.
    setFullBatch(null);
    try {
      const res = await markNotesPaid([...selected]);
      setConfirmPaidOpen(false);
      setSelected(new Set());
      fetchDashboard(logisticCompanyId);
      if (res.updated > 0) {
        showToast(t('{{n}} nota(s) marcadas como pagadas.', { n: res.updated }), 'success');
      }
      // Los rechazos no son un error de la request: son notas que cambiaron de estado
      // entre que se cargó la lista y se confirmó. Hay que nombrarlas, no esconderlas.
      if (res.skipped.length > 0) {
        showToast(skippedReasonsMessage(res.skipped, t), 'warning');
      }
    } catch (err) {
      // Éxito parcial: un lote marcó notas y otro falló después. Esas notas YA quedaron
      // pagadas, así que el modal se cierra, el resumen se refresca (el store ya recargó la
      // lista) y se dice cuántas quedaron y por qué falló el resto.
      if (err?.partial?.updated > 0) {
        setConfirmPaidOpen(false);
        setSelected(new Set());
        fetchDashboard(logisticCompanyId);
        showToast(
          t('{{n}} notas marcadas; falló el resto: {{motivo}}', {
            n: err.partial.updated,
            motivo: apiErrorMessage(err, t('error desconocido')),
          }),
          'error',
        );
        if (err.partial.skipped?.length > 0) {
          showToast(skippedReasonsMessage(err.partial.skipped, t), 'warning');
        }
        return;
      }
      showToast(apiErrorMessage(err, t('No se pudieron marcar las notas como pagadas.')), 'error');
    }
  };

  const goToNote = (id) => navigate(`/admin/cobro/${id}`);

  // DateRangeFilter entrega (startYMD, endYMD, preset).
  const handleRangeChange = (start, end, preset) => {
    setDatePreset(preset);
    setDateFrom(start);
    setDateTo(end);
  };
  const handleClearDate = () => {
    setDatePreset('');
    setDateFrom('');
    setDateTo('');
  };

  return (
    <div className="bill-page">
      <PageHeader
        className="mb-6"
        icon={<Receipt className="w-6 h-6 text-fg-muted" />}
        title={t('Notas de cobro')}
        subtitle={
          loadFailed
            ? undefined
            : `${filtered.length} ${filtered.length === 1 ? t('nota de cobro') : t('notas de cobro')}`
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/cobro/cobranza')}
              iconLeft={<BellRing className="w-4 h-4" />}
              title={t('Vencimientos y recordatorios de pago a los comercios')}
            >
              {t('Cobranza')}
            </Button>
            <Button onClick={() => setWizardOpen(true)} iconLeft={<Plus className="w-4 h-4" />}>
              {t('Cobrar a comercios')}
            </Button>
          </>
        }
      />

      <CobroDashboard
        ownerId={logisticCompanyId}
        operatorCurrency={operatorCurrency}
        onSelectDebtor={handleSelectDebtor}
        onConfigureDebtor={setRuleDrawer}
        activeListFilter={narrowing}
        onListFilter={toggleDashboardListFilter}
      />

      {/* En mobile los filtros se colapsan tras un disclosure para que la lista
          asome en el primer viewport. */}
      {isMobile && (
        <Button
          variant="outline"
          className="mb-3 w-full justify-between"
          onClick={() => setFiltersOpen((o) => !o)}
          iconLeft={<SlidersHorizontal className="w-4 h-4" />}
          aria-expanded={filtersOpen}
        >
          <span className="flex-1 text-left">{t('Filtros')}</span>
          <ChevronDown
            className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
          />
        </Button>
      )}

      {/* Barra de filtros: fecha + modo + comercio.
          Sin `--clip` (overflow:hidden) para no recortar el popover de fechas. */}
      <div
        className="bill-card"
        style={{ overflow: 'visible', display: isMobile && !filtersOpen ? 'none' : undefined }}
      >
        <div className="bill-filters" style={{ gridTemplateColumns: '1fr', gap: '1rem' }}>
          <div className="bill-field">
            <label className="bill-label">{t('Periodo (fecha de la nota)')}</label>
            <DateRangeFilter
              value={datePreset}
              presets={DATE_PRESETS_COBRO}
              onChange={handleRangeChange}
              onClear={handleClearDate}
              customRange={dateFrom && dateTo ? { start: dateFrom, end: dateTo } : null}
            />
            {!dateFrom && (
              <span className="bill-hint" style={{ marginTop: '0.375rem' }}>
                {t('Sin filtro de fecha — mostrando todas las notas.')}
              </span>
            )}
          </div>
          <div className="bill-form-row" style={{ gap: '1rem' }}>
            <div className="bill-field">
              <label className="bill-label">{t('Modo de cobro')}</label>
              <Select
                value={filters.chargeMode}
                onChange={(e) => setFilters({ chargeMode: e.target.value })}
              >
                {CHARGE_MODE_FILTERS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {t(o.label)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="bill-field" style={{ flex: 2 }}>
              <label className="bill-label">{t('Comercio')}</label>
              <SearchableSelect
                options={[{ value: '', label: t('Todos los comercios') }, ...companyOptions]}
                value={filters.companyId || ''}
                onChange={(v) => {
                  setDashboardFilter(null);
                  setFilters({ companyId: v, holdingId: '' });
                }}
                placeholder={t('Buscar comercio…')}
                isClearable
                noOptionsText={t('Sin comercios')}
                filterOption={filterCommerceOption}
                formatOptionLabel={formatCommerceOptionLabel}
              />
            </div>
            {/* Notas consolidadas de un holding. Solo si hay alguno que elegir (o si el filtro
                ya está puesto: sin su selector no se podría quitar). */}
            {(holdingOptions.length > 0 || filters.holdingId) && (
              <div className="bill-field">
                <label className="bill-label" htmlFor="cobro-holding-filter">
                  {t('Holding')}
                </label>
                <Select
                  id="cobro-holding-filter"
                  value={filters.holdingId || ''}
                  onChange={(e) => {
                    setDashboardFilter(null);
                    setFilters({ holdingId: e.target.value, companyId: '' });
                  }}
                >
                  <option value="">{t('Todas las notas')}</option>
                  {holdingOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {(dashboardFilterActive || dashboardListFilterLabel) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {dashboardFilterActive && (
            <FilterChip
              label={t('Filtrado por: {{name}}', { name: dashboardFilterName })}
              removeLabel={
                dashboardFilterIsHolding
                  ? t('Quitar filtro por holding')
                  : t('Quitar filtro por comercio')
              }
              onRemove={clearDashboardFilter}
            />
          )}
          {dashboardListFilterLabel && (
            <FilterChip
              label={t('Filtrado por: {{name}}', { name: dashboardListFilterLabel })}
              removeLabel={t('Quitar el filtro "{{name}}"', { name: dashboardListFilterLabel })}
              onRemove={() => setNarrowing('')}
            />
          )}
        </div>
      )}

      {/* Filtro por estado de pago (clickeable).
          En mobile: fila de chips scrolleable horizontal (los KPI-cards ocupaban
          demasiado alto). En desktop: los KPI-cards de siempre. */}
      {isMobile ? (
        <div
          className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-1 px-1"
          role="group"
          aria-label={t('Filtrar por estado')}
        >
          {STATUS_CARDS.map((c) => {
            const active = activeStatusCard === c.value;
            const rawCount = c.value === '' ? statusCountTotal : counts[c.value];
            const count = rawCount === null ? '—' : (rawCount ?? 0);
            const Icon = c.icon;
            return (
              <button
                key={c.value || 'all'}
                type="button"
                aria-pressed={active}
                onClick={() => selectStatusCard(c.value)}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'border-brand-500 bg-brand-50/60 text-brand-700'
                    : 'border-border bg-bg text-fg-muted'
                }`}
              >
                <Icon className="w-3.5 h-3.5" aria-hidden />
                <span>{t(c.label)}</span>
                <span className="tabular-nums font-semibold">{count}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <KpiFilterCards
          className="mb-4 sm:grid-cols-3 lg:grid-cols-7"
          cards={STATUS_CARDS.map((c) => ({ ...c, label: t(c.label) }))}
          counts={counts}
          total={statusCountTotal}
          active={activeStatusCard}
          onChange={selectStatusCard}
        />
      )}

      {/* Las tarjetas se solapan: una vencida también es impaga o parcial. Sin esta aclaración
          "Vencidas" parece un estado más y los números no suman. */}
      {narrowing === OVERDUE_CARD && (
        <div className="bill-note mb-3">
          {t('Notas vencidas: impagas o con pago parcial cuyo vencimiento ya pasó.')}
        </div>
      )}
      {/* El número de la tarjeta del resumen es de todo el operador y la lista respeta el
          comercio, el modo y el período: sin esta aclaración parecen no cuadrar. */}
      {narrowing === 'dueSoon' && (
        <div className="bill-note mb-3">
          {t('Notas por vencer: impagas o con pago parcial que vencen en los próximos 7 días. El resumen es de todo el operador; la lista respeta el comercio, el modo y el período elegidos.')}
        </div>
      )}
      {narrowing === 'multipleUnpaid' && (
        <div className="bill-note mb-3">
          {t('Notas impagas (también las que tienen un pago en revisión) de los comercios con más de una nota impaga. El resumen cuenta comercios de todo el operador; la lista muestra sus notas y respeta el comercio, el modo y el período elegidos.')}
        </div>
      )}

      {/* Aviso de lote truncado */}
      {!loading && !loadFailed && truncated && (
        <div className="bill-note mb-3">
          {t('Mostrando {{n}} de {{total}} notas. Afiná por comercio o modo para ver el resto.', {
            n: notes.length,
            total: notesCount,
          })}
        </div>
      )}

      {/* Resumen de la vista filtrada */}
      {!loading && !loadFailed && filtered.length > 0 && (
        <div className="bill-strip" role="group" aria-label={t('Resumen')}>
          <div className="bill-strip__item">
            <span className="bill-strip__icon"><Receipt className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('Notas')}</span>
            <span className="bill-strip__value">{filtered.length}</span>
          </div>
          <div className="bill-strip__item">
            <span className="bill-strip__icon bill-strip__icon--accent"><Wallet className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('Total emitido')}</span>
            <span className="bill-strip__value bill-td-amount">{formatCurrency(strip.total)}</span>
          </div>
          <div className="bill-strip__item">
            <span className="bill-strip__icon" style={{ color: 'rgb(var(--status-success))' }}>
              <CheckCircle2 className="w-4 h-4" />
            </span>
            <span className="bill-strip__label">{t('Cobrado')}</span>
            <span className="bill-strip__value bill-td-amount">{formatCurrency(strip.cobrado)}</span>
          </div>
          <div className="bill-strip__item">
            <span className="bill-strip__icon bill-strip__icon--warning"><Clock className="w-4 h-4" /></span>
            <span className="bill-strip__label">{t('Pendiente')}</span>
            <span className="bill-strip__value bill-td-amount">{formatCurrency(strip.pendiente)}</span>
          </div>
        </div>
      )}

      {/* Tabla / Cards */}
      <div className="bill-card bill-card--clip" ref={listRef}>
        {loading ? (
          <TableSkeleton rows={8} columns={7} className="border-0" />
        ) : loadFailed ? (
          <div role="alert">
            <EmptyState
              icon={<AlertTriangle />}
              title={t('No se pudieron cargar las notas de cobro.')}
              description={notesError}
              action={
                <Button variant="outline" onClick={fetchNotes}>
                  {t('Reintentar')}
                </Button>
              }
            />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Receipt />}
            title={noNotesAtAll ? t('No hay notas de cobro') : t('Sin resultados para estos filtros')}
            description={
              noNotesAtAll
                ? t('Genera tu primera nota para empezar a cobrarles a tus comercios por el periodo trabajado.')
                : t('Prueba con otro estado, periodo o comercio.')
            }
            action={
              noNotesAtAll ? (
                <Button onClick={() => setWizardOpen(true)} iconLeft={<Plus className="w-4 h-4" />}>
                  {t('Cobrar a comercios')}
                </Button>
              ) : undefined
            }
          />
        ) : isMobile ? (
          <div className="bill-cards">
            {pageItems.map((n) => (
              <div
                key={n._id}
                className="bill-card-item bill-card-item--clickable"
                role="button"
                tabIndex={0}
                onClick={() => goToNote(n._id)}
                onKeyDown={(e) => { if (e.key === 'Enter') goToNote(n._id); }}
              >
                <div className="bill-card-item__head">
                  {/* Marcable: stopPropagation para que tildar no navegue. No marcable: la casilla
                      no recibe clics (pointer-events) y el clic sube a la tarjeta → detalle, que
                      es donde el motivo dice que se confirma; el motivo va en el title del span. */}
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}
                    onClick={isMarkable(n) ? (e) => e.stopPropagation() : undefined}
                    title={isMarkable(n) ? undefined : t(NOT_MARKABLE_REASON[n.paymentStatus] ?? '')}
                  >
                    <input
                      type="checkbox"
                      aria-label={t('Seleccionar la nota #{{folio}}', { folio: n.folio ?? '—' })}
                      checked={selected.has(n._id)}
                      onChange={() => toggleNote(n._id)}
                      disabled={!isMarkable(n)}
                      style={isMarkable(n) ? { cursor: 'pointer' } : { pointerEvents: 'none' }}
                    />
                  </span>
                  <span className="bill-card-item__title">
                    {resolveCompanyName(n, companyMap) || <span className="bill-td-sub">{t('Comercio')}</span>}
                    {isHoldingNote(n) && (
                      <span className="bill-badge bill-badge--info bill-badge--sm ms-2">{t('Holding')}</span>
                    )}
                  </span>
                  <PaymentStatusBadge note={n} size="sm" />
                </div>
                <div className="bill-card-item__rows">
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Folio')}</span>
                    <span className="bill-card-item__v bill-td-track">#{n.folio ?? '—'}</span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Modo')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {t(CHARGE_MODE_LABELS[n.chargeMode] ?? n.chargeMode)}
                    </span>
                  </div>
                  <div className="bill-card-item__field bill-card-item__field--full">
                    <span className="bill-card-item__k">{t('Periodo')}</span>
                    <span className="bill-card-item__v bill-td-muted">
                      {formatDate(n.periodStart)} → {formatDate(n.periodEnd)}
                    </span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Total')}</span>
                    <span className="bill-card-item__v bill-td-amount">
                      {formatCurrency(n.totalAmount, n.currency)}
                    </span>
                  </div>
                  <div className="bill-card-item__field">
                    <span className="bill-card-item__k">{t('Saldo')}</span>
                    <span className="bill-card-item__v bill-td-amount">
                      {formatCurrency(amountDueOf(n), n.currency)}
                    </span>
                  </div>
                  {dueDateStatus(n) && (
                    <div className="bill-card-item__field">
                      <span className="bill-card-item__k">{t('Vencimiento')}</span>
                      <DueDateHint note={n} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bill-grid" role="table" style={{ '--bill-cols': GRID_COLS }}>
            <div className="bill-grid__head" role="row">
              <span
                className="bill-grid__hcell"
                role="columnheader"
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="checkbox"
                  aria-label={t('Seleccionar las notas de esta página')}
                  checked={allPageSelected}
                  ref={(el) => { if (el) el.indeterminate = somePageSelected; }}
                  onChange={togglePage}
                  disabled={pageMarkable.length === 0}
                  style={{ cursor: pageMarkable.length === 0 ? 'default' : 'pointer' }}
                />
              </span>
              <span className="bill-grid__hcell" role="columnheader">{t('Folio')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Comercio')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Periodo')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Modo')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Total')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Saldo')}</span>
              <span className="bill-grid__hcell" role="columnheader">{t('Estado')}</span>
              <span className="bill-grid__hcell bill-grid__hcell--end" role="columnheader">{t('Acciones')}</span>
            </div>
            {pageItems.map((n) => (
              <div
                key={n._id}
                className="bill-grid__row bill-grid__row--clickable"
                role="row"
                onClick={() => goToNote(n._id)}
              >
                {/* Marcable: stopPropagation obligatorio, la fila entera navega al detalle y sin
                    esto tildar la casilla te saca de la lista. No marcable: la casilla no recibe
                    clics (pointer-events) y el clic sube a la fila → detalle, que es donde el
                    motivo dice que se confirma; el motivo va en el title del span. */}
                <span
                  role="cell"
                  style={{ display: 'flex', alignItems: 'center' }}
                  onClick={isMarkable(n) ? (e) => e.stopPropagation() : undefined}
                  title={isMarkable(n) ? undefined : t(NOT_MARKABLE_REASON[n.paymentStatus] ?? '')}
                >
                  <input
                    type="checkbox"
                    aria-label={t('Seleccionar la nota #{{folio}}', { folio: n.folio ?? '—' })}
                    checked={selected.has(n._id)}
                    onChange={() => toggleNote(n._id)}
                    disabled={!isMarkable(n)}
                    style={isMarkable(n) ? { cursor: 'pointer' } : { pointerEvents: 'none' }}
                  />
                </span>
                <span className="bill-td-track" role="cell">
                  #{n.folio ?? '—'} <CopyButton value={n.folio} label={t('Copiar folio')} copiedMessage={t('Folio copiado')} />
                </span>
                <span className="bill-td-strong" role="cell">
                  {resolveCompanyName(n, companyMap) || <span className="bill-td-sub">—</span>}
                  {/* Nota consolidada: el deudor es el holding, no un comercio. */}
                  {isHoldingNote(n) && (
                    <span className="bill-badge bill-badge--info bill-badge--sm ms-2">{t('Holding')}</span>
                  )}
                </span>
                <span className="bill-td-muted" role="cell">
                  {formatDate(n.periodStart)}
                  <br />
                  <span className="bill-td-sub">→ {formatDate(n.periodEnd)}</span>
                </span>
                <span className="bill-td-muted" role="cell">
                  {t(CHARGE_MODE_LABELS[n.chargeMode] ?? n.chargeMode)}
                </span>
                <span className="bill-grid__cell--end bill-td-amount" role="cell">
                  {formatCurrency(n.totalAmount, n.currency)}
                </span>
                <span className="bill-grid__cell--end bill-td-amount" role="cell">
                  {formatCurrency(amountDueOf(n), n.currency)}
                </span>
                <span role="cell">
                  <PaymentStatusBadge note={n} />
                  <DueDateHint note={n} className="mt-1" />
                </span>
                <span className="bill-grid__cell--actions" role="cell" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => goToNote(n._id)}>
                    {t('Ver')}
                  </Button>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Paginación (cliente) */}
        {!loading && !loadFailed && totalPages > 1 && (
          <div className="bill-pagination">
            <Button
              size="sm"
              variant="outline"
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label={t('Anterior')}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>{t('Página')} {safePage} {t('de')} {totalPages}</span>
            <Button
              size="sm"
              variant="outline"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label={t('Siguiente')}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Barra de selección. Sticky al pie, aparece sólo con algo seleccionado. */}
      {selected.size > 0 && (
        <div className="bill-pay-selbar">
          <span className="text-sm text-fg">
            {t('{{n}} nota(s) seleccionadas', { n: selected.size })}{' '}
            <span className="bill-td-amount">{formatCurrency(selectedAmount, currencyOfSelection)}</span>
            {holdingNotesSelected > 0 && (
              <span className="bill-hint d-block">
                {t('{{n}} nota(s) de holding no reciben recordatorios (llegan próximamente): no entran al programar.', {
                  n: holdingNotesSelected,
                })}
              </span>
            )}
            {selectedNotes.length > 1 && !singleDebtor && (
              <span className="bill-hint d-block">
                {t('El abono requiere notas de un solo deudor.')}
              </span>
            )}
            {selectedNotes.length > 1 && mixedCurrencies && (
              <span className="bill-hint d-block">
                {t('El abono requiere notas de una sola moneda.')}
              </span>
            )}
            {tooManyForAllocation && (
              <span className="bill-hint d-block">{t('El abono admite hasta 20 notas.')}</span>
            )}
          </span>
          {/* Atajo explícito: el checkbox de cabecera sólo alcanza la página visible, así
              que sin esto marcar 300 notas exigiría pasar 12 páginas a mano. */}
          {filteredMarkable.length > selected.size && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set(filteredMarkable))}
              disabled={markingPaid}
            >
              {t('Seleccionar las {{n}} del filtro', { n: filteredMarkable.length })}
            </Button>
          )}
          {/* flexWrap: con 3 botones no entran en una fila en un teléfono (375 px). */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 8,
              marginLeft: 'auto',
            }}
          >
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelected(new Set())}
              disabled={markingPaid}
            >
              {t('Limpiar')}
            </Button>
            {/* Las seleccionables son justamente las cobrables (impagas y parciales): las
                mismas que admiten un recordatorio. */}
            <Button
              size="sm"
              variant="outline"
              iconLeft={<CalendarClock className="w-4 h-4" />}
              onClick={() => setScheduleOpen(true)}
              disabled={markingPaid || reminderNotes.length === 0}
            >
              {t('Programar recordatorio')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              iconLeft={<Wallet className="w-4 h-4" />}
              onClick={openAllocate}
              disabled={!canAllocate || allocating || markingPaid}
              title={
                tooManyForAllocation
                  ? t('El abono admite hasta 20 notas.')
                  : !singleDebtor
                    ? t('Selecciona notas de un solo deudor para registrar un abono.')
                    : mixedCurrencies
                      ? t('Selecciona notas de una sola moneda para registrar un abono.')
                      : undefined
              }
            >
              {t('Registrar abono a varias notas')}
            </Button>
            <Button
              size="sm"
              loading={markingPaid}
              iconLeft={<CheckCircle2 className="w-4 h-4" />}
              onClick={() => setConfirmPaidOpen(true)}
            >
              {t('Marcar como pagadas')}
            </Button>
          </div>
        </div>
      )}

      {/* Confirmación obligatoria: no hay forma de desmarcar una nota pagada desde el
          panel, así que un click de más no tiene vuelta atrás. */}
      <Modal
        open={confirmPaidOpen}
        onClose={() => setConfirmPaidOpen(false)}
        size="sm"
        title={t('Marcar notas como pagadas')}
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmPaidOpen(false)} disabled={markingPaid}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleMarkPaid} loading={markingPaid}>
              {t('Marcar como pagadas')}
            </Button>
          </>
        }
      >
        <p className="mb-2" style={{ marginTop: 0 }}>
          {t('Vas a marcar')}{' '}
          <strong>{t('{{n}} nota(s) de cobro', { n: selected.size })}</strong> {t('por un saldo de')}{' '}
          <strong>{formatCurrency(selectedAmount, currencyOfSelection)}</strong>.
        </p>
        {mixedCurrencies && (
          <p className="bill-hint mb-2">
            {t('Ojo: la selección mezcla notas de distintas monedas, así que ese total es sólo la suma de los importes.')}
          </p>
        )}
        <p className="bill-hint mb-2">
          {t('Se registra un pago por el saldo pendiente de cada nota con la fecha de hoy. Al comercio NO se le envía ningún correo ni comprobante: es un registro administrativo.')}
        </p>
        <p className="bill-hint mb-0">
          {t('Esta acción no se puede deshacer desde el panel.')}
        </p>
      </Modal>

      {/* Abono repartido entre varias notas del mismo deudor y moneda. */}
      <Modal
        open={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        size="md"
        title={t('Registrar abono a varias notas')}
        footer={
          <>
            <Button variant="outline" onClick={() => setAllocateOpen(false)} disabled={allocating}>
              {t('Cancelar')}
            </Button>
            <Button onClick={handleAllocate} loading={allocating} disabled={!canAllocate}>
              {t('Registrar abono')}
            </Button>
          </>
        }
      >
        {!canAllocate ? (
          <p className="bill-hint" role="alert" style={{ color: 'rgb(var(--status-error))' }}>
            {tooManyForAllocation
              ? t('El abono admite hasta 20 notas.')
              : !singleDebtor
                ? t('Las notas deben ser de un solo deudor.')
                : t('Las notas deben estar en la misma moneda.')}
          </p>
        ) : (
          <>
            <p className="bill-hint" style={{ marginTop: 0 }}>
              {t('El abono se reparte en orden de folio ascendente. Cada nota recibe hasta su saldo; el sobrante queda como saldo a favor del comercio.')}
            </p>

            {allocError && (
              <p className="bill-hint" role="alert" style={{ color: 'rgb(var(--status-error))' }}>
                {allocError}
              </p>
            )}

            <div className="bill-field">
              <label className="bill-label" htmlFor="alloc-amount">
                {t('Monto del abono')} <span className="text-status-error">*</span>
              </label>
              <Input
                id="alloc-amount"
                type="number"
                min="1"
                step={currencyOfSelection === 'CLP' ? '1' : '0.01'}
                value={allocAmount}
                onChange={(e) => setAllocAmount(e.target.value)}
                autoFocus
              />
              <span className="bill-hint">
                {t('Saldo total de las notas seleccionadas')}:{' '}
                {formatCurrency(allocationTotalDue, currencyOfSelection)}
              </span>
            </div>

            <div className="bill-field">
              <label className="bill-label" htmlFor="alloc-date">
                {t('Fecha de pago')} <span className="text-status-error">*</span>
              </label>
              <Input
                id="alloc-date"
                type="date"
                max={tomorrowYMD()}
                value={allocDate}
                onChange={(e) => setAllocDate(e.target.value)}
              />
            </div>

            {!allocUseCredit && (
              <div className="bill-field">
                <label className="bill-label" htmlFor="alloc-method">{t('Método de pago')}</label>
                <Select
                  id="alloc-method"
                  value={allocMethod}
                  onChange={(e) => setAllocMethod(e.target.value)}
                >
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {t(paymentMethodLabelKey(method))}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={allocUseCredit}
                onChange={(e) => {
                  setAllocUseCredit(e.target.checked);
                  setAllocCreditId('');
                  setAllocError('');
                }}
              />
              <span className="bill-label" style={{ margin: 0 }}>{t('Usar saldo a favor')}</span>
            </label>

            {allocUseCredit && (
              <div className="bill-field" style={{ marginTop: '0.5rem' }}>
                {allocCreditsLoading ? (
                  <span className="bill-hint">{t('Cargando saldos a favor…')}</span>
                ) : allocCreditsError ? (
                  <span className="bill-hint" style={{ color: 'rgb(var(--status-error))' }}>
                    {t(allocCreditsError)}
                  </span>
                ) : allocationCredits.length === 0 ? (
                  <span className="bill-hint">
                    {t('No hay saldos a favor abiertos para este deudor en esta moneda.')}
                  </span>
                ) : (
                  <>
                    <label className="bill-label" htmlFor="alloc-credit">
                      {t('Saldo a favor disponible')}
                    </label>
                    <Select
                      id="alloc-credit"
                      value={allocCreditId}
                      onChange={(e) => setAllocCreditId(e.target.value)}
                    >
                      <option value="">{t('Selecciona un saldo a favor.')}</option>
                      {allocationCredits.map((credit) => (
                        <option key={credit._id} value={credit._id}>
                          {formatCurrency(
                            Number(credit.remaining) || 0,
                            credit.currency ?? currencyOfSelection,
                          )}
                          {credit.paymentReference ? ` · ${credit.paymentReference}` : ''}
                        </option>
                      ))}
                    </Select>
                  </>
                )}
              </div>
            )}

            <div className="bill-field">
              <label className="bill-label" htmlFor="alloc-reference">{t('Referencia (opcional)')}</label>
              <Input
                id="alloc-reference"
                value={allocReference}
                maxLength={120}
                onChange={(e) => setAllocReference(e.target.value)}
              />
            </div>

            <div className="bill-field">
              <label className="bill-label" htmlFor="alloc-comment">{t('Comentario (opcional)')}</label>
              <textarea
                id="alloc-comment"
                className="bill-textarea"
                rows={2}
                maxLength={500}
                value={allocComment}
                onChange={(e) => setAllocComment(e.target.value)}
              />
            </div>

            <div className="bill-note" style={{ marginTop: '0.75rem' }}>
              <div className="bill-td-strong" style={{ marginBottom: '0.375rem' }}>
                {t('Vista previa del reparto')}
              </div>
              {allocationPreview.rows.length === 0 ? (
                <span className="bill-hint">{t('Ingresa un monto para ver cómo se reparte.')}</span>
              ) : (
                <>
                  {allocationPreview.rows.map((row) => (
                    <div
                      key={row.noteId}
                      className="d-flex justify-content-between align-items-center"
                      style={{ gap: '1rem', fontSize: '0.875rem', padding: '0.125rem 0' }}
                    >
                      <span className="bill-td-track">#{row.folio ?? '—'}</span>
                      <span className="bill-td-muted">
                        {t('Saldo')}: {formatCurrency(row.due, currencyOfSelection)}
                      </span>
                      <span className="bill-td-amount">
                        {formatCurrency(row.applied, currencyOfSelection)}
                      </span>
                    </div>
                  ))}
                  <div
                    className="d-flex justify-content-between"
                    style={{ gap: '1rem', marginTop: '0.5rem', fontSize: '0.875rem' }}
                  >
                    <span className="bill-td-strong">{t('Monto aplicado')}</span>
                    <span className="bill-td-amount">
                      {formatCurrency(allocationPreview.applied, currencyOfSelection)}
                    </span>
                  </div>
                  {allocationPreview.leftover > 0 && !allocUseCredit && (
                    <div className="bill-hint" style={{ marginTop: '0.375rem' }}>
                      {t('Sobrante que quedará como saldo a favor')}:{' '}
                      <strong>
                        {formatCurrency(allocationPreview.leftover, currencyOfSelection)}
                      </strong>
                    </div>
                  )}
                  {allocationPreview.leftover > 0 && allocUseCredit && (
                    <div className="bill-hint" style={{ marginTop: '0.375rem', color: 'rgb(var(--status-error))' }}>
                      {t('Con saldo a favor el monto no puede dejar sobrante.')}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </Modal>

      <ScheduleReminderModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        notes={reminderNotes}
        onScheduled={() => {
          setScheduleOpen(false);
          setSelected(new Set());
        }}
      />

      <CommerceCollectionRuleDrawer
        open={!!ruleDrawer}
        onClose={() => setRuleDrawer(null)}
        companyId={ruleDrawer?.companyId ?? null}
        companyName={ruleDrawer?.companyName ?? null}
      />

      <GenerateCobroWizard
        isOpen={wizardOpen}
        toggle={() => setWizardOpen(false)}
        onGenerated={() => {
          setFullBatch(null);
          fetchNotes();
          fetchDashboard(logisticCompanyId);
        }}
      />
    </div>
  );
};

export default CobroNotesList;
